import { useState } from "react"
import { api } from "../../api/client.ts"
import type { Case, CaseListItem } from "../../api/types.ts"
import { useApiQuery, useAsyncAction, useDebounced } from "../../hooks/index.ts"
import { useI18n } from "../../i18n/index.tsx"
import { candidateQuery } from "../../lib/orphan-link.ts"
import { formatTracking } from "../../lib/tracking.ts"
import { Button, Drawer, EmptyState, ErrorNote, Field, Input, Skeleton } from "../../ui/index.ts"

/** 목록이 들고 있는 만큼. 이을 때 서버가 다시 다 확인하므로 화면은 보여 줄 것만 안다. */
export type OrphanRow = {
  id: number
  trackingNo: string
  scanCount: number
  firstSeenAt: string
  lastSeenAt: string
}

/**
 * 무적화물 한 상자를 접수 건에 잇는 판.
 *
 * 창고 사람이 하는 일은 **어느 건인지 고르는 것 하나**다. 고르면 서버가 번호를 채우고
 * 입고완료로 올리고 목록에서 뺀다 — 화면이 세 번 요청하지 않는다(중간에 끊기면 상자가
 * 반쯤 처리된 상태로 남는다).
 *
 * 어느 건을 보여 줄지는 `lib/orphan-link.ts` 가 정한다(테스트가 지킨다).
 * 번호가 이미 적힌 건은 일부러 빼므로, 뜬 건은 누르면 반드시 이어진다.
 */
export function OrphanLinkPanel({
  orphan,
  onClose,
  onLinked,
}: {
  orphan: OrphanRow
  onClose: () => void
  /** 이어진 뒤. 보통 무적화물 목록과 오늘 스캔을 다시 읽는다. */
  onLinked: (linked: Case) => void
}) {
  const { t, locale } = useI18n()
  const [search, setSearch] = useState("")
  // 입력칸은 글자를 그대로 보여 주고, 조회만 멎은 뒤에 따라간다.
  const settled = useDebounced(search, 250)
  const candidates = useApiQuery<{ items: CaseListItem[]; total: number }>(
    candidateQuery(settled),
  )
  const link = useAsyncAction()

  const items = candidates.data?.items ?? []

  async function linkTo(target: CaseListItem) {
    const ok = await link.run(async () => {
      const res = await api.post<{ case: Case }>(`/api/scan/orphans/${orphan.id}/link`, {
        caseId: target.id,
      })
      onLinked(res.case)
    })
    if (ok) onClose()
  }

  return (
    <Drawer title={t("orphan.linkTitle")} onClose={onClose}>
      <p className="orphan-link__box">
        <strong>{formatTracking(orphan.trackingNo)}</strong>
        <span className="muted">{t("scan.orphanCount", { count: orphan.scanCount })}</span>
      </p>
      <p className="muted">{t("orphan.linkHelp")}</p>

      <Field label={t("orphan.searchLabel")} htmlFor="orphan-search">
        <Input
          id="orphan-search"
          autoFocus
          autoComplete="off"
          placeholder={t("orphan.searchPlaceholder")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Field>

      {link.error ? <ErrorNote error={link.error} /> : null}

      {candidates.loading && candidates.data === null ? (
        <Skeleton lines={4} />
      ) : items.length === 0 ? (
        <EmptyState
          icon="search"
          title={t("orphan.noCandidates")}
          description={t("orphan.noCandidatesHint")}
        />
      ) : (
        <ul className="orphan-link__list">
          {items.map((one) => (
            <li key={one.id} className="orphan-link__item">
              <div className="orphan-link__who">
                <strong>
                  #{one.seq} · {one.orderNo}
                </strong>
                <span className="muted">
                  {one.customerName} · {locale === "ja" ? one.orgNameJa : one.orgNameKo} ·{" "}
                  {one.receivedOn}
                </span>
              </div>
              <Button
                size="sm"
                disabled={link.busy}
                onClick={() => void linkTo(one)}
              >
                {t("orphan.link")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  )
}
